import styled from 'styled-components';
import { Image } from 'antd';
import { WizardCardProps } from './WizardCard';

interface StyledProps {
  colorScheme: WizardCardProps['colorScheme'];
}

interface ContainerProps {
  rotation: number;
  orbitRadius: number;
  width: number;
}

export const CardContainer = styled.div<ContainerProps>`
  /* If orbitRadius is 0, use static positioning (flat layout), otherwise use absolute (orbital layout) */
  position: ${props => props.orbitRadius === 0 ? 'static' : 'absolute'};
  width: ${props => props.width}px;
  height: auto;
  
  /* Orbital layout transforms */
  ${props => props.orbitRadius > 0 && `
    transform: rotate(${props.rotation}deg) translateY(-${props.orbitRadius}px);
    transform-origin: 50% ${props.orbitRadius}px;
    left: 50%;
    top: 50%;
    margin-left: -${props.width / 2}px;
    margin-top: -${props.width / 2}px;
  `}
  
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  z-index: 10;

  &:hover {
    ${props => props.orbitRadius > 0 
      ? `transform: rotate(${props.rotation}deg) translateY(-${props.orbitRadius}px) rotate(-${props.rotation}deg) scale(1.35);`
      : `transform: scale(1.08) translateY(-5px);`
    }
    z-index: 20;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }

  /* Mobile styles */
  @media (max-width: 767px) {
    position: static;
    width: 100% !important;
    margin: 0 0 20px 0;
    transform: none !important;
    
    &:hover {
      transform: scale(1.02) !important;
    }
  }
`;

export const StyledCard = styled.div<StyledProps>`
  width: 100%;
  min-height: 280px; /* Minimum height allowing expansion */
  padding: 24px 20px;
  border-radius: 16px;
  box-shadow: 0 8px 24px ${props => props.colorScheme.borderColor}26; /* 26 = 15% opacity in hex */
  border: 2px solid ${props => props.colorScheme.borderColor};
  text-align: center;
  background: linear-gradient(135deg, ${props => props.colorScheme.backgroundGradient[0]} 0%, ${props => props.colorScheme.backgroundGradient[1]} 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const TopGradientBar = styled.div<StyledProps>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, ${props => props.colorScheme.gradientColors[0]}, ${props => props.colorScheme.gradientColors[1]});
  border-radius: 16px 16px 0 0;
`;

export const TopColorBar = styled.div<StyledProps>`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  height: 5px;
  background: linear-gradient(90deg, ${props => props.colorScheme.gradientColors[0]}, ${props => props.colorScheme.gradientColors[1]});
  border-radius: 16px 16px 0 0;
  z-index: 1;
`;

export const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  flex-shrink: 0;
  justify-content: center;
`;

export const MainImage = styled(Image)`
  width: 70px;
  object-fit: contain;
`;

export const StepImage = styled(Image)`
  width: 36px;
  object-fit: contain;
`;

export const CardTitle = styled.div<StyledProps>`
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 16px;
  font-weight: 700;
  color: ${props => props.colorScheme.titleColor};
  line-height: 1.2;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
`;

export const CardDescription = styled.div`
  font-size: 11px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;
